import React from "react"
import { isMobileOnly } from "react-device-detect"
import clsx from "clsx"
import { NavLink } from "react-router-dom"
import { useTranslation } from "react-i18next"

import * as BikeApi from "features/bike/api/BikeApi"
import { privateRoutes } from "router/routes"

import { useModal, useAlert, useBikeStoreProcess } from "hooks"
import { Markdown } from "components/Elements/Markdown"
import dateService from "services/date"
import { getLanguage } from "utils/i18n"

import {
	ONE_DAY_IN_SECONDS,
	BUY_PROPERTY_TRANSFER_REQUEST,
	REQUEST_ONGOING,
	REQUEST_CANCEL,
	REQUEST_FORCED,
	REQUEST_REJECTED,
	REQUEST_ACCEPTED,
	REQUEST_RENEW,
} from "utils/constants"
import { getSteps, TimelineSteps, RequestModal } from "../../Partials"
import { ErrorResponse } from "models"

type BuyRequestedCardProps = {
	isBO: boolean
}

export const BuyRequestedCard: React.FC<BuyRequestedCardProps> = ({ isBO }) => {
	const { fetchBike, bikeProcessState } = useBikeStoreProcess()
	const { currentBike: bike } = bikeProcessState
	const { showModal } = useModal()
	const { t } = useTranslation()
	const { alert } = useAlert()
	const defaultCurrentLanguage = getLanguage()

	const bikeTransferStatus = bike?.property_transfer_request?.status
	const isBikeTransferRequestOnGoing = bikeTransferStatus === REQUEST_ONGOING
	const isBikeTransferRequestAccepted =
		bikeTransferStatus === REQUEST_ACCEPTED || bikeTransferStatus === REQUEST_FORCED
	const isBikeTransferRequestRejected = bikeTransferStatus === REQUEST_REJECTED
	const isBikeTransferRequestForced = bikeTransferStatus === REQUEST_FORCED

	// A request is expired after 24h
	// Transform request date in timestamp
	const bikeTransferRequestDateTime = dateService.getTimestamp(
		bike?.property_transfer_request?.request_date
	)
	// Add 1 day to the requested date
	const bikeTransferRequestExpiredDate = bikeTransferRequestDateTime + ONE_DAY_IN_SECONDS
	// Compare expired request date with the current date
	const isExpiredRequest = new Date().getTime() > bikeTransferRequestExpiredDate

	// We extract the link from the store-id
	const bikeTransferRequestStoreId = bike?.property_transfer_request?.store_id
	const bikeTransferRequestStoreLink = bikeTransferRequestStoreId.startsWith("ST-")
		? bikeTransferRequestStoreId.slice(3)
		: null

	const currentRequestSteps = React.useMemo(() => {
		// We update the timeline (steps) when bike is updated
		const neededSteps = [
			"SEND",
			"WAITING",
			...(() => {
				if (isBikeTransferRequestRejected) return ["REJECTED"]
				if (isBikeTransferRequestForced) return ["FORCED"]
				if (isBikeTransferRequestOnGoing && isExpiredRequest) return ["EXPIRED"]
				return ["ACCEPTED"]
			})(),
		]
		return getSteps(bike).filter((step) => neededSteps.includes(step.id))
	}, [bike, defaultCurrentLanguage]) // eslint-disable-line

	const handleBackToStoreRequest = () => {
		showModal(RequestModal, {
			propertyTransferRequestContext: BUY_PROPERTY_TRANSFER_REQUEST,
			modalType: REQUEST_RENEW,
			callbackAndSend: async () => {
				try {
					const bikePropertyTransferRequest = {
						context: BUY_PROPERTY_TRANSFER_REQUEST,
					}

					const updatedBikePayload = {
						bike_id: bike.code,
						row_version: bike.row_version,
						property_transfer_request: bikePropertyTransferRequest,
					}

					const { getError } = await BikeApi.editBike(updatedBikePayload)
					if (getError()) throw getError()

					alert("success", t("back_in_store.modal_success_message", { ns: "bike" }))
					fetchBike(bike.code)
				} catch (error) {
					console.error(error)
					const erroMsg = (error as ErrorResponse)?.message?.toString()
					if (erroMsg) alert("error", erroMsg)
				}
			},
		})
	}

	const handleBackToStoreCancel = () => {
		showModal(RequestModal, {
			propertyTransferRequestContext: BUY_PROPERTY_TRANSFER_REQUEST,
			modalType: REQUEST_CANCEL,
			callbackAndSend: async () => {
				try {
					const updatedBikePayload = {
						bike_id: bike.code,
						row_version: bike.row_version,
						property_transfer_request: null,
					}

					const { getError } = await BikeApi.editBike(updatedBikePayload)
					if (getError()) throw getError()

					alert("success", t("buy_bike.modal_cancel_message", { ns: "bike" }))
					fetchBike(bike.code)
				} catch (error) {
					console.error(error)
					const erroMsg = (error as ErrorResponse)?.message?.toString()
					if (erroMsg) alert("error", erroMsg)
				}
			},
		})
	}

	return (
		<div className="c-card p-5 md:p-12 w-full">
			<h2 className="c-h2 font-semibold mb-5 uppercase">
				{t("buy_bike.title", { ns: "bike" })}
			</h2>

			{isBO && (
				<p>
					{t("back_and_buy.description_is_admin", {
						propertyTransferType: BUY_PROPERTY_TRANSFER_REQUEST.toLowerCase(),
						ns: "bike",
					})}
					{bikeTransferRequestStoreLink && (
						<NavLink
							to={privateRoutes.partner.basePath + "/" + bikeTransferRequestStoreLink}
							className="underline font-bold"
						>
							{bike?.property_transfer_request?.social_reason}
						</NavLink>
					)}
					{!bikeTransferRequestStoreLink && (
						<span className="font-bold">
							{bike?.property_transfer_request?.social_reason}
						</span>
					)}
				</p>
			)}

			{!isBO && (
				<>
					{isBikeTransferRequestAccepted && (
						<p>{t("back_in_store.requested_description_accepted", { ns: "bike" })}</p>
					)}
					{isBikeTransferRequestRejected && (
						<p>
							{t("back_and_buy.requested_description_rejected", {
								propertyTransferType: BUY_PROPERTY_TRANSFER_REQUEST.toLowerCase(),
								ns: "bike",
							})}
						</p>
					)}

					{isExpiredRequest && !isBikeTransferRequestAccepted && (
						<Markdown>
							{t("back_and_buy.requested_description_no_answer_with_account", {
								propertyTransferType: BUY_PROPERTY_TRANSFER_REQUEST.toLowerCase(),
								ns: "bike",
							})}
						</Markdown>
					)}

					{isBikeTransferRequestOnGoing && !isExpiredRequest && (
						<Markdown>
							{t("back_and_buy.requested_description_with_account", {
								propertyTransferType: BUY_PROPERTY_TRANSFER_REQUEST.toLowerCase(),
								ns: "bike",
							})}
						</Markdown>
					)}
				</>
			)}

			{currentRequestSteps && <TimelineSteps steps={currentRequestSteps} />}

			{
				/* Buttons are displayed when a request is on going, rejected or expired */
				!isBikeTransferRequestAccepted && !isBO && (
					<div
						className={clsx("flex justify-between mt-12", { "flex-col": isMobileOnly })}
					>
						<button
							data-cy="handle-buy-to-store-cancel"
							type="button"
							className="c-btn c-btn--white-bordered px-7 mb-5 lg:m-0"
							onClick={handleBackToStoreCancel}
						>
							{t("back_and_buy.cancel_button", { ns: "bike" })}
						</button>

						<button
							type="button"
							data-cy="handle-buy-to-store-request"
							className={`c-btn  px-7 mb-5 lg:m-0
                            ${
								isBikeTransferRequestRejected || isExpiredRequest
									? "c-btn--secondary"
									: "c-btn--primary"
							}`}
							onClick={handleBackToStoreRequest}
						>
							{t("back_and_buy.renew_button", { ns: "bike" })}
						</button>
					</div>
				)
			}
		</div>
	)
}
